import { useEffect, useMemo, useState } from 'react';
import { useApp } from 'contexts/app';
import ProductOptionService from 'services/product_option';

const productOptionService = new ProductOptionService();

const normalizeRows = (rows = []) => (rows || []).map((r) => ({ id: r?.id, values: [...(r?.values || [])] }));

const useComponent = ({ value, productOptions, onApply, onAttributeCreated }) => {
  const { prompt, showNotify } = useApp();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [localOptions, setLocalOptions] = useState([]);

  useEffect(() => {
    setLocalOptions((productOptions || []).filter((x) => x?.id !== 'default'));
  }, [productOptions]);

  const optionDefs = useMemo(() => localOptions, [localOptions]);

  const selectedIds = rows.map((r) => String(r?.id)).filter(Boolean);

  const openSheet = () => {
    setRows(normalizeRows(value));
    setOpen(true);
  };

  const closeSheet = () => setOpen(false);

  const onToggleAttribute = (attrId, checked) => {
    const key = String(attrId);
    setRows((prev) => {
      if (checked) {
        if (prev.some((x) => String(x?.id) === key)) return prev;
        if (prev.length >= 2) {
          showNotify('Chỉ được chọn tối đa 2 thuộc tính');
          return prev;
        }
        return [...prev, { id: attrId, values: [] }];
      }
      return prev.filter((x) => String(x?.id) !== key);
    });
  };

  const createNewAttribute = async () => {
    if (creating) return;
    prompt({
      title: 'Thêm mới thuộc tính',
      label: 'Nhập tên thuộc tính',
      onOk: async (text) => {
        const title = String(text || '').trim();
        if (!title) return;
        try {
          setCreating(true);
          const created = await productOptionService.create({ title });
          const latest = await productOptionService.list();
          const filteredLatest = (latest || []).filter((x) => x?.id !== 'default');
          setLocalOptions(filteredLatest);
          await onAttributeCreated?.();
          const createdId =
            created?.id ||
            filteredLatest.find((x) => String(x?.title || '').toLowerCase() === title.toLowerCase())?.id;
          if (createdId) {
            setRows((prev) => {
              if (prev.some((x) => String(x?.id) === String(createdId))) return prev;
              if (prev.length >= 2) {
                showNotify('Đã tạo thuộc tính mới. Chỉ được chọn tối đa 2 thuộc tính');
                return prev;
              }
              return [...prev, { id: createdId, values: [] }];
            });
          }
        } finally {
          setCreating(false);
        }
      },
    });
  };

  const apply = () => {
    const cleanRows = rows.filter((r) => r?.id);
    const nextValue = cleanRows.map((r) => ({
      id: r.id,
      values: (r?.values || []).filter(Boolean),
    }));
    onApply?.(nextValue);
    setOpen(false);
  };

  return {
    open,
    openSheet,
    closeSheet,
    optionDefs,
    selectedIds,
    onToggleAttribute,
    createNewAttribute,
    apply,
    creating,
  };
};

export default useComponent;
